import { cn } from '@/lib/utils';

interface CTABannerProps {
  section: any;
}

export default function CTABanner({ section }: CTABannerProps) {
  const content = section.content ?? {};
  const { headline, description, bgColor, bgImage } = content;
  // The wizard and the admin builder write snake_case (`cta_text`/`cta_link`,
  // matching every other section and MarketingCtaBand); only this component
  // read camelCase, so seeded closing banners rendered with no button at all.
  // Accept both.
  const ctaText = content.cta_text ?? content.ctaText;
  const ctaLink = content.cta_link ?? content.ctaLink;
  const headingId = `cta-banner-heading-${section.id}`;

  const hasBgImage = !!bgImage;
  const hasBgColor = !!bgColor;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'relative overflow-hidden',
        !hasBgImage && !hasBgColor && 'bg-gradient-to-r from-sage-700 to-sage-900'
      )}
      style={{
        ...(hasBgColor && !hasBgImage ? { backgroundColor: bgColor } : {}),
        ...(hasBgImage
          ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : {}),
      }}
    >
      {/* Darkened gradient overlay for background images */}
      {hasBgImage && (
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      )}

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 text-center">
        {(headline || section.title) && (
          <h2
            id={headingId}
            className="font-display text-3xl font-bold text-white sm:text-4xl"
          >
            {headline || section.title}
          </h2>
        )}
        {(description || section.subtitle) && (
          <p className="mt-4 text-white/85 max-w-2xl mx-auto text-lg">
            {description || section.subtitle}
          </p>
        )}
        {ctaText && ctaLink && (
          <a
            href={ctaLink}
            className={cn(
              'mt-8 inline-flex items-center rounded-lg px-8 py-3 text-base font-semibold transition-colors',
              'bg-cta-fg text-cta hover:bg-cta-fg',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-sage-700'
            )}
          >
            {ctaText}
          </a>
        )}
      </div>
    </section>
  );
}
